import React, { useEffect, useMemo, useState } from 'react';
import { FilterChip } from '@/components/ui/FilterChip';
import { Icon } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import { SearchBar } from '@/components/ui/SearchBar';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { Colors } from '@/constants/Colors';
import {
  COMMENT_TEMPLATE_CATEGORIES,
  COMMENT_TEMPLATE_CATEGORY_LABELS,
  isCommentTemplateUsed,
  type CommentTemplate,
  type CommentTemplateCategory,
} from '@/constants/observationCommentTemplates';
import { useTheme } from '@/hooks/useTheme';

interface CommentTemplatesSheetProps {
  visible: boolean;
  onClose: () => void;
  templates: readonly CommentTemplate[];
  existingComments: string;
  onInsert: (texts: string[]) => void;
  disabled?: boolean;
  advancedMode: boolean;
  onAdvancedModeChange: (enabled: boolean) => void;
}

function normalizeSearch(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

export function CommentTemplatesSheet({
  visible,
  onClose,
  templates,
  existingComments,
  onInsert,
  disabled = false,
  advancedMode,
  onAdvancedModeChange,
}: CommentTemplatesSheetProps) {
  const colors = useTheme();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CommentTemplateCategory | 'all'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!visible) {
      setSearch('');
      setCategory('all');
      setSelectedIds(new Set());
    }
  }, [visible]);

  const filteredTemplates = useMemo(() => {
    const q = normalizeSearch(search);
    return templates.filter((template) => {
      if (category !== 'all' && template.category !== category) return false;
      if (!q) return true;
      return normalizeSearch(template.text).includes(q);
    });
  }, [templates, search, category]);

  const frequentTemplates = useMemo(() => {
    if (search.trim() || category !== 'all') return [];
    return templates.filter((t) => t.frequent);
  }, [templates, search, category]);

  const listTemplates = useMemo(() => {
    if (frequentTemplates.length === 0) return filteredTemplates;
    const frequentIds = new Set(frequentTemplates.map((t) => t.id));
    return filteredTemplates.filter((t) => !frequentIds.has(t.id));
  }, [filteredTemplates, frequentTemplates]);

  const selectableCount = useMemo(() => {
    let count = 0;
    for (const id of selectedIds) {
      const template = templates.find((t) => t.id === id);
      if (template && !isCommentTemplateUsed(existingComments, template.text)) count += 1;
    }
    return count;
  }, [selectedIds, templates, existingComments]);

  const toggleTemplate = (template: CommentTemplate) => {
    if (disabled || isCommentTemplateUsed(existingComments, template.text)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(template.id)) next.delete(template.id);
      else next.add(template.id);
      return next;
    });
  };

  const handleInsert = () => {
    const texts = templates
      .filter((t) => selectedIds.has(t.id) && !isCommentTemplateUsed(existingComments, t.text))
      .map((t) => t.text);
    if (texts.length === 0) return;
    onInsert(texts);
    onClose();
  };

  const renderRow = (template: CommentTemplate) => {
    const used = isCommentTemplateUsed(existingComments, template.text);
    const selected = selectedIds.has(template.id);
    const rowDisabled = disabled || used;

    return (
      <button
        key={template.id}
        type="button"
        onClick={() => toggleTemplate(template)}
        disabled={rowDisabled}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          width: '100%',
          textAlign: 'left',
          padding: '12px 4px',
          borderBottom: `1px solid ${colors.border}`,
          opacity: rowDisabled ? 0.5 : 1,
          cursor: rowDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            border: `2px solid ${used ? colors.border : selected ? Colors.primary : colors.border}`,
            backgroundColor: selected && !used ? Colors.primary : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: 2,
          }}
        >
          {used ? (
            <Icon name="checkmark-circle" size={18} color={colors.textSecondary} />
          ) : selected ? (
            <Icon name="checkmark" size={14} color="#ffffff" />
          ) : null}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, lineHeight: 1.45, color: colors.text }}>{template.text}</div>
          {template.pro ? (
            <span
              style={{
                display: 'inline-block',
                marginTop: 6,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                padding: '2px 6px',
                borderRadius: 4,
                backgroundColor: `${Colors.primary}18`,
                color: Colors.primary,
              }}
            >
              PRO
            </span>
          ) : null}
          {used ? (
            <div style={{ fontSize: 11, marginTop: 4, color: colors.textSecondary }}>Déjà dans le commentaire</div>
          ) : null}
        </div>
      </button>
    );
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 16px 12px',
            borderBottom: `1px solid ${colors.border}`,
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px 12px' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: colors.text }}>Commentaires types</div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: disabled ? 'default' : 'pointer',
                  userSelect: 'none',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: advancedMode ? Colors.primary : colors.textSecondary }}>
                  Scout
                </span>
                <ToggleSwitch
                  id="comment-templates-pro"
                  checked={advancedMode}
                  disabled={disabled}
                  ariaLabel="Activer les commentaires scout et analyse"
                  onChange={onAdvancedModeChange}
                />
              </label>
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>
              {advancedMode
                ? 'Tribune + langage scout / analyse tactique'
                : 'Vue tribune — langage simple (parents, supporters)'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              border: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.surface,
            }}
          >
            <Icon name="close" size={22} color={colors.textSecondary} />
          </button>
        </div>

        <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
          <SearchBar value={search} onChangeText={setSearch} placeholder="Rechercher un commentaire…" />
        </div>

        <div
          style={{
            display: 'flex',
            gap: 0,
            overflowX: 'auto',
            padding: '12px 16px',
            flexShrink: 0,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {COMMENT_TEMPLATE_CATEGORIES.map((cat) => (
            <FilterChip
              key={cat}
              label={COMMENT_TEMPLATE_CATEGORY_LABELS[cat]}
              isActive={category === cat}
              onPress={() => setCategory(cat)}
            />
          ))}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 12px', WebkitOverflowScrolling: 'touch' }}>
          {filteredTemplates.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 14, color: colors.textSecondary }}>
              Aucun commentaire ne correspond à votre recherche.
            </div>
          ) : (
            <>
              {frequentTemplates.length > 0 ? (
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 1,
                      color: `${Colors.primary}cc`,
                      padding: '8px 4px 4px',
                    }}
                  >
                    FRÉQUENTES
                  </div>
                  {frequentTemplates.map(renderRow)}
                </div>
              ) : null}
              {listTemplates.length > 0 ? (
                <div>
                  {frequentTemplates.length > 0 ? (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1,
                        color: `${Colors.primary}cc`,
                        padding: '8px 4px 4px',
                      }}
                    >
                      TOUS LES COMMENTAIRES
                    </div>
                  ) : null}
                  {listTemplates.map(renderRow)}
                </div>
              ) : null}
            </>
          )}
        </div>

        <div
          style={{
            padding: '12px 16px',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            borderTop: `1px solid ${colors.border}`,
            flexShrink: 0,
            backgroundColor: colors.background,
          }}
        >
          <button
            type="button"
            onClick={handleInsert}
            disabled={disabled || selectableCount === 0}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 12,
              backgroundColor: Colors.primary,
              color: '#ffffff',
              fontSize: 15,
              fontWeight: 700,
              opacity: disabled || selectableCount === 0 ? 0.5 : 1,
              cursor: disabled || selectableCount === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {selectableCount > 0 ? `Insérer (${selectableCount})` : 'Insérer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
